import { useCallback, useEffect, useRef } from "react";
import { impactSoundUrl } from "../data";
import type { Layout, Point, ProjectileProfile } from "../types";
import {
  BALL_RADIUS,
  FLOOR_HEIGHT,
  MAX_PULL,
  WORLD_HEIGHT,
  WORLD_WIDTH,
  clampDrag,
  createEngine,
  detonate,
  launchVelocity,
  remaining,
  stepEffects,
  stepEngine,
  type EngineState,
} from "./engine";
import { canvasTheme } from "./theme";
import { THEME_EVENT } from "../themes/theme";

interface Props {
  layout: Layout;
  profile: ProjectileProfile | null;
  armed: boolean;
  fireNonce: number;
  resetNonce: number;
  reduceMotion: boolean;
  onShotStart: () => void;
  onState: (blocks: number, goals: number, message?: string) => void;
  onShotEnd: (cleared: boolean) => void;
  onDetonate?: () => void;
}

interface Viewport {
  width: number;
  height: number;
  scale: number;
  offsetX: number;
  offsetY: number;
}

function viewportFor(width: number, height: number): Viewport {
  const scale = Math.min(width / WORLD_WIDTH, height / WORLD_HEIGHT);
  return { width, height, scale, offsetX: (width - WORLD_WIDTH * scale) / 2, offsetY: (height - WORLD_HEIGHT * scale) / 2 };
}

function toWorld(point: Point, viewport: Viewport): Point {
  return {
    x: (point.x - viewport.offsetX) / viewport.scale,
    y: WORLD_HEIGHT - (point.y - viewport.offsetY) / viewport.scale,
  };
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function drawCell(context: CanvasRenderingContext2D, cell: EngineState["cells"][number]) {
  if (cell.remaining <= 0) return;
  const x = cell.x - cell.width / 2;
  const y = cell.y - cell.height / 2;
  context.save();
  context.globalAlpha = 1 - cell.flash * 0.12;
  roundedRect(context, x, y, cell.width, cell.height, 3);
  context.fillStyle = canvasTheme.materials[cell.kind];
  context.fill();
  context.lineWidth = 1.5;
  context.strokeStyle = canvasTheme.frame;
  context.stroke();

  context.strokeStyle = canvasTheme.pattern;
  context.lineWidth = 1;
  if (cell.kind === "glass") {
    context.beginPath(); context.moveTo(x + cell.width * 0.16, y + cell.height * 0.78); context.lineTo(x + cell.width * 0.78, y + cell.height * 0.22); context.stroke();
  } else if (cell.kind === "laminated" || cell.kind === "dampener") {
    for (const inset of [0.18, 0.3]) { roundedRect(context, x + cell.width * inset, y + cell.height * inset, cell.width * (1 - inset * 2), cell.height * (1 - inset * 2), 4); context.stroke(); }
  } else if (cell.kind === "brick") {
    context.beginPath(); context.moveTo(x + 4, cell.y); context.lineTo(x + cell.width - 4, cell.y); context.stroke();
    context.beginPath(); context.moveTo(cell.x, y + 3); context.lineTo(cell.x, cell.y); context.moveTo(cell.x - 10, cell.y); context.lineTo(cell.x - 10, y + cell.height - 3); context.stroke();
  } else if (cell.kind === "resonance") {
    context.beginPath(); context.arc(cell.x, cell.y, 8, 0, Math.PI * 2); context.stroke();
    context.beginPath(); context.arc(cell.x, cell.y, 15, 0, Math.PI * 2); context.stroke();
  }

  context.fillStyle = cell.kind === "core" ? canvasTheme.paper : canvasTheme.ink;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = "400 22px sans-serif";
  if (cell.kind === "core") context.fillText("★", cell.x, cell.y + 1);
  else if (cell.durability > 1) {
    for (let i = 0; i < cell.durability; i += 1) {
      context.globalAlpha = i < cell.remaining ? 1 : 0.22;
      context.beginPath(); context.arc(cell.x + (i - (cell.durability - 1) / 2) * 9, y + cell.height * 0.76, 2.5, 0, Math.PI * 2); context.fill();
    }
  }
  context.restore();
}

function drawScene(canvas: HTMLCanvasElement, state: EngineState, viewport: Viewport, profile: ProjectileProfile | null, armed: boolean, dragging: boolean, reduceMotion: boolean) {
  const context = canvas.getContext("2d");
  if (!context) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, viewport.width, viewport.height);
  context.save();
  context.translate(viewport.offsetX, viewport.offsetY + WORLD_HEIGHT * viewport.scale);
  context.scale(viewport.scale, -viewport.scale);

  context.fillStyle = canvasTheme.paper;
  context.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
  context.lineWidth = 1 / viewport.scale;
  for (let x = 25; x < WORLD_WIDTH; x += 25) {
    context.strokeStyle = x % 125 === 0 ? canvasTheme.gridMajor : canvasTheme.grid;
    context.beginPath(); context.moveTo(x, FLOOR_HEIGHT); context.lineTo(x, WORLD_HEIGHT); context.stroke();
  }
  for (let y = 25; y < WORLD_HEIGHT; y += 25) {
    context.strokeStyle = y % 125 === 0 ? canvasTheme.gridMajor : canvasTheme.grid;
    context.beginPath(); context.moveTo(0, y); context.lineTo(WORLD_WIDTH, y); context.stroke();
  }

  context.fillStyle = canvasTheme.floor;
  context.fillRect(0, 0, WORLD_WIDTH, FLOOR_HEIGHT);
  context.strokeStyle = canvasTheme.ink;
  context.beginPath(); context.moveTo(0, FLOOR_HEIGHT); context.lineTo(WORLD_WIDTH, FLOOR_HEIGHT); context.stroke();

  const anchor = state.layout.slingshot;
  context.lineCap = "round";
  context.strokeStyle = canvasTheme.sling;
  context.lineWidth = 7;
  context.beginPath(); context.moveTo(anchor.x, anchor.y - 115); context.lineTo(anchor.x, anchor.y - 58); context.lineTo(anchor.x - 18, anchor.y - 14); context.moveTo(anchor.x, anchor.y - 58); context.lineTo(anchor.x + 18, anchor.y - 14); context.stroke();
  context.fillStyle = canvasTheme.sling;
  roundedRect(context, anchor.x - 30, anchor.y - 128, 60, 18, 3); context.fill();

  if ((dragging || state.ball.active === false) && (armed || dragging)) {
    context.strokeStyle = canvasTheme.sling;
    context.lineWidth = 3;
    context.beginPath(); context.moveTo(anchor.x - 18, anchor.y - 14); context.lineTo(state.ball.x, state.ball.y); context.lineTo(anchor.x + 18, anchor.y - 14); context.stroke();
  }

  if (dragging && profile) {
    const velocity = launchVelocity(anchor, state.ball, profile);
    if (velocity) {
      for (let index = 1; index <= 16; index += 1) {
        const t = index * 0.05;
        const decay = Math.exp(-0.1 * t);
        const gravityVelocity = -650 / 0.1;
        const x = state.ball.x + velocity.x * ((1 - decay) / 0.1);
        const y = state.ball.y + gravityVelocity * t + (velocity.y - gravityVelocity) * ((1 - decay) / 0.1);
        if (x < 0 || x > WORLD_WIDTH || y < FLOOR_HEIGHT || y > WORLD_HEIGHT) break;
        context.globalAlpha = Math.max(0.25, 0.92 - index * 0.038);
        context.fillStyle = canvasTheme.ink;
        context.beginPath(); context.arc(x, y, Math.max(3.2, 6.2 - index * 0.14), 0, Math.PI * 2); context.fill();
      }
      context.globalAlpha = 1;
    }
  }

  for (const cell of state.cells) drawCell(context, cell);

  for (const particle of state.particles) {
    context.globalAlpha = particle.life;
    context.fillStyle = particle.color;
    roundedRect(context, particle.x - particle.size / 2, particle.y - particle.size / 3, particle.size, particle.size * 0.66, 1); context.fill();
  }
  context.globalAlpha = 1;

  if (state.blast) {
    context.globalAlpha = state.blast.life;
    context.strokeStyle = canvasTheme.signal;
    context.lineWidth = 5;
    context.beginPath(); context.arc(state.blast.x, state.blast.y, state.blast.radius * (1.15 - state.blast.life * 0.15), 0, Math.PI * 2); context.stroke();
    context.globalAlpha = 1;
  }

  if (!state.ball.active || state.ball.elapsed < 5) {
    if (armed && !dragging && !state.ball.active) {
      context.strokeStyle = canvasTheme.ink;
      context.lineWidth = 1.5;
      context.setLineDash([5, 5]);
      const pulse = reduceMotion ? 1 : 0.95 + Math.sin(performance.now() / 300) * 0.09;
      context.beginPath(); context.arc(state.ball.x, state.ball.y, 32 * pulse, 0, Math.PI * 2); context.stroke();
      context.setLineDash([]);
    }
    context.fillStyle = canvasTheme.signal;
    context.strokeStyle = canvasTheme.paper;
    context.lineWidth = 2;
    context.beginPath(); context.arc(state.ball.x, state.ball.y, BALL_RADIUS, 0, Math.PI * 2); context.fill(); context.stroke();
  }

  if (state.message && state.ball.active) {
    context.save();
    context.scale(1, -1);
    context.fillStyle = canvasTheme.ink;
    context.font = '500 18px "IBM Plex Sans JP", sans-serif';
    context.textAlign = "center";
    context.fillText(state.message, state.ball.x, -state.ball.y - 34);
    context.restore();
  }
  context.restore();
}

export default function PhysicsCanvas(props: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef(createEngine(props.layout));
  const viewportRef = useRef(viewportFor(930, 834));
  const frameRef = useRef(0);
  const readyFrameRef = useRef(0);
  const effectFrameRef = useRef(0);
  const effectTimeRef = useRef(0);
  const lastTimeRef = useRef(0);
  const draggingRef = useRef(false);
  const playedHitRef = useRef(0);
  const propsRef = useRef(props);
  propsRef.current = props;

  const notifyState = useCallback((message?: string) => {
    const counts = remaining(engineRef.current);
    propsRef.current.onState(counts.blocks, counts.goals, message);
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawScene(canvas, engineRef.current, viewportRef.current, propsRef.current.profile, propsRef.current.armed, draggingRef.current, propsRef.current.reduceMotion);
  }, []);

  const animateEffects = useCallback((time: number) => {
    const dt = effectTimeRef.current ? (time - effectTimeRef.current) / 1000 : 1 / 60;
    effectTimeRef.current = time;
    const hasEffects = stepEffects(engineRef.current, dt);
    draw();
    if (hasEffects) effectFrameRef.current = requestAnimationFrame(animateEffects);
    else effectTimeRef.current = 0;
  }, [draw]);

  const finish = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    notifyState(engineRef.current.message);
    const cleared = remaining(engineRef.current).goals === 0;
    propsRef.current.onShotEnd(cleared);
    cancelAnimationFrame(effectFrameRef.current);
    effectTimeRef.current = 0;
    effectFrameRef.current = requestAnimationFrame(animateEffects);
  }, [animateEffects, notifyState]);

  const tick = useCallback((time: number) => {
    const profile = propsRef.current.profile;
    if (!profile) return;
    const dt = lastTimeRef.current ? (time - lastTimeRef.current) / 1000 : 1 / 60;
    lastTimeRef.current = time;
    const before = engineRef.current.cells.filter((cell) => cell.remaining > 0).length;
    const result = stepEngine(engineRef.current, profile, dt);
    const after = engineRef.current.cells.filter((cell) => cell.remaining > 0).length;
    if (after < before && performance.now() - playedHitRef.current > 70) {
      playedHitRef.current = performance.now();
      void new Audio(impactSoundUrl).play().catch(() => undefined);
      notifyState(engineRef.current.message);
    }
    draw();
    if (result === "finished") finish();
    else frameRef.current = requestAnimationFrame(tick);
  }, [draw, finish, notifyState]);

  const launch = useCallback((position: Point) => {
    const profile = propsRef.current.profile;
    const state = engineRef.current;
    if (!profile || state.ball.active || !propsRef.current.armed) return;
    cancelAnimationFrame(effectFrameRef.current);
    effectTimeRef.current = 0;
    const velocity = launchVelocity(state.layout.slingshot, position, profile);
    if (!velocity) {
      state.ball.x = state.layout.slingshot.x;
      state.ball.y = state.layout.slingshot.y;
      draggingRef.current = false;
      draw();
      return;
    }
    state.ball = { x: position.x, y: position.y, vx: velocity.x, vy: velocity.y, active: true, elapsed: 0, initialSpeed: Math.hypot(velocity.x, velocity.y) };
    state.contactingCells.clear();
    state.message = "";
    draggingRef.current = false;
    lastTimeRef.current = 0;
    propsRef.current.onShotStart();
    frameRef.current = requestAnimationFrame(tick);
  }, [draw, tick]);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;
    const resize = () => {
      const rect = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      viewportRef.current = viewportFor(rect.width, rect.height);
      draw();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();
    return () => observer.disconnect();
  }, [draw]);

  useEffect(() => {
    cancelAnimationFrame(frameRef.current);
    cancelAnimationFrame(effectFrameRef.current);
    effectTimeRef.current = 0;
    engineRef.current = createEngine(props.layout);
    draggingRef.current = false;
    notifyState();
    draw();
  }, [props.layout, props.resetNonce, draw, notifyState]);

  useEffect(() => {
    const state = engineRef.current;
    if (!state.ball.active && !draggingRef.current) {
      state.ball.x = state.layout.slingshot.x;
      state.ball.y = state.layout.slingshot.y;
      draw();
    }
  }, [props.profile, props.armed, draw]);

  useEffect(() => {
    if (props.fireNonce === 0) return;
    const anchor = engineRef.current.layout.slingshot;
    launch(clampDrag(anchor, { x: anchor.x + 125, y: anchor.y - 28 }));
  }, [props.fireNonce, launch]);

  useEffect(() => {
    if (props.reduceMotion || !props.armed || engineRef.current.ball.active) return;
    const animateReady = () => { draw(); readyFrameRef.current = requestAnimationFrame(animateReady); };
    readyFrameRef.current = requestAnimationFrame(animateReady);
    return () => cancelAnimationFrame(readyFrameRef.current);
  }, [props.armed, props.reduceMotion, draw]);

  const triggerDetonate = useCallback(() => {
    const profile = propsRef.current.profile;
    const state = engineRef.current;
    if (!profile || !detonate(state, profile)) return false;
    notifyState(state.message);
    propsRef.current.onDetonate?.();
    // Cleared so finish() does not report the same BLAST again.
    state.message = "";
    draw();
    return true;
  }, [draw, notifyState]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== "Space" || event.repeat || !engineRef.current.ball.active) return;
      if (triggerDetonate()) event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [triggerDetonate]);

  useEffect(() => {
    window.addEventListener(THEME_EVENT, draw);
    return () => window.removeEventListener(THEME_EVENT, draw);
  }, [draw]);

  useEffect(() => () => {
    cancelAnimationFrame(frameRef.current);
    cancelAnimationFrame(readyFrameRef.current);
    cancelAnimationFrame(effectFrameRef.current);
  }, []);

  const pointerPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return toWorld({ x: event.clientX - rect.left, y: event.clientY - rect.top }, viewportRef.current);
  };

  return (
    <div ref={hostRef} className="physics-host">
      <canvas
        ref={canvasRef}
        className={props.armed ? "physics-canvas is-armed" : "physics-canvas"}
        aria-label="物理ゲーム盤。弾が準備できたらオレンジのボールを引いて離します"
        role="application"
        tabIndex={0}
        onPointerDown={(event) => {
          const state = engineRef.current;
          if (state.ball.active) { triggerDetonate(); return; }
          if (!props.armed) return;
          const point = pointerPoint(event);
          if (Math.hypot(point.x - state.ball.x, point.y - state.ball.y) > 72) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          draggingRef.current = true;
          state.ball = { ...state.ball, ...clampDrag(state.layout.slingshot, point) };
          draw();
        }}
        onPointerMove={(event) => {
          if (!draggingRef.current) return;
          const state = engineRef.current;
          state.ball = { ...state.ball, ...clampDrag(state.layout.slingshot, pointerPoint(event)) };
          draw();
        }}
        onPointerUp={(event) => {
          if (!draggingRef.current) return;
          event.currentTarget.releasePointerCapture(event.pointerId);
          launch(engineRef.current.ball);
        }}
        onPointerCancel={() => {
          const state = engineRef.current;
          draggingRef.current = false;
          state.ball.x = state.layout.slingshot.x;
          state.ball.y = state.layout.slingshot.y;
          draw();
        }}
      />
    </div>
  );
}
